import {
  ColDef,
  GridOptions,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import {
  heliosClientSideRowConfig,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import { formatDates } from '@helios/common-api'
import { NfColumnTypes } from '@northfork/ag-grid'
import { isNullOrEmptyString } from '@northfork/basics'
import { Daterange, ToastService } from '@northfork/ng-basics'
import { subDays } from 'date-fns'
import { IceCorporateAction } from '../models/ice-corporate-action'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  BasicClient,
  IceCorporateActionsService,
} from '../services/ice-corporate-actions.service'

@Component({
  selector: 'hs-ice-corporate-actions-grid',
  templateUrl: './ice-corporate-actions-grid.component.html',
  styleUrls: ['./ice-corporate-actions-grid.component.scss'],
})
export class IceCorporateActionsGridComponent implements OnInit, OnDestroy {
  @Output()
  iceCorporateActionSelected$ = new EventEmitter<
    IceCorporateAction | undefined
  >()

  @Output()
  loadStarted$ = new EventEmitter<void>()

  @Output()
  selectedClientIdsChanged$ = new EventEmitter<number[]>()

  @Output()
  dateRangeChanged$ = new EventEmitter<Daterange>()

  @Output()
  rowDataChanged$ = new EventEmitter<IceCorporateAction[]>()

  @Input()
  rowData: IceCorporateAction[] = []
  clients: BasicClient[] = []

  @Input()
  selectedClientIds: number[] = []

  loading = false

  lookbackDays?: number

  @Input()
  dateRange?: Daterange

  private readonly destroySubject$ = new Subject<void>()
  private static readonly LOAD_TIMEOUT_MS = 10 * 60 * 1000
  private loadTimeoutHandle: ReturnType<typeof setTimeout> | undefined
  private loadGeneration = 0

  columnDefs: ColDef[] = [
    { field: 'type', headerName: 'Event Category' },
    { field: 'eventType', headerName: 'Event Type' },
    {
      field: 'action',
      headerName: 'Action Type',
      valueFormatter: params =>
        isNullOrEmptyString(params.value as string | null | undefined)
          ? 'Mandatory'
          : params.value,
    },
    {
      field: 'effectiveDate',
      headerName: 'Effective Date',
      type: NfColumnTypes.Date,
      valueFormatter: params =>
        formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy'),
      tooltipValueGetter: params =>
        formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy'),
    },
    { field: 'parentSecurity', headerName: 'Parent Security' },
  ]

  gridOptions: GridOptions = {
    ...heliosClientSideRowConfig,
    defaultColDef: {
      ...heliosDefaultColDef,
      sortable: true,
    },
    columnDefs: this.columnDefs,
    rowSelection: 'single',
    suppressRowClickSelection: false,
    overlayNoRowsTemplate: 'No data',
    onFirstDataRendered: params => params.api.sizeColumnsToFit(),
    onSelectionChanged: (event: SelectionChangedEvent) => {
      const selected = event.api.getSelectedNodes()
      if (selected.length > 0) {
        this.onRowSelected(selected[0].data as IceCorporateAction)
      }
    },
  }

  constructor(
    private readonly iceService: IceCorporateActionsService,
    private readonly toastService: ToastService,
  ) {}

  get lookbackCutoff(): string {
    if (this.lookbackDays === undefined) return ''
    return subDays(new Date(), this.lookbackDays).toISOString().split('T')[0]
  }

  get showLookbackWarning(): boolean {
    return (
      this.lookbackDays !== undefined &&
      !!this.dateRange?.beginDate &&
      this.dateRange.beginDate < this.lookbackCutoff
    )
  }

  clientSearchFn = (term: string, item: BasicClient): boolean => {
    const normalized = term.trim().toLowerCase()
    if (!normalized) return true
    return (
      item.name.toLowerCase().includes(normalized) ||
      item.id.toString().startsWith(normalized)
    )
  }

  ngOnInit(): void {
    this.iceService
      .getConfig()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe({
        next: config =>
          (this.lookbackDays = config.maxPastPositionLookbackDays),
        error: err =>
          console.error('Failed to load ICE corporate-actions config', err),
      })

    this.iceService
      .getClients()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe({
        next: clients => (this.clients = clients),
        error: err => {
          console.error('Failed to load client list', err)
          this.toastService.error('Failed to load client list')
        },
      })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    clearTimeout(this.loadTimeoutHandle)
  }

  async loadCorporateActions(): Promise<void> {
    if (this.selectedClientIds.length === 0) return
    const dateRange = this.dateRange
    if (!dateRange?.beginDate || !dateRange?.endDate) return
    const clientIdString = this.selectedClientIds.join(',')

    this.loadStarted$.emit()
    this.loading = true
    this.rowData = []
    this.rowDataChanged$.emit(this.rowData)

    clearTimeout(this.loadTimeoutHandle)
    const generation = ++this.loadGeneration
    this.loadTimeoutHandle = setTimeout(() => {
      if (this.loadGeneration !== generation) return
      this.loadGeneration++ // invalidate this request's continuation
      this.loading = false
      this.loadTimeoutHandle = undefined
      this.toastService.error('Request timed out. Try Load again.')
    }, IceCorporateActionsGridComponent.LOAD_TIMEOUT_MS)

    try {
      const actions = await this.iceService
        .getByClient(clientIdString, dateRange.beginDate, dateRange.endDate)
        .pipe(takeUntil(this.destroySubject$))
        .toPromise()
      if (this.loadGeneration !== generation) return
      clearTimeout(this.loadTimeoutHandle)
      this.loadTimeoutHandle = undefined
      this.rowData = actions ?? []
      this.rowDataChanged$.emit(this.rowData)
      this.loading = false
    } catch (error) {
      if (this.loadGeneration !== generation) return
      clearTimeout(this.loadTimeoutHandle)
      this.loadTimeoutHandle = undefined
      this.loading = false
      this.toastService.error(
        'Failed to fetch ICE corporate actions. Error: ' + error,
      )
    }
  }

  onRowSelected(action: IceCorporateAction): void {
    this.iceCorporateActionSelected$.emit(action)
  }

  onSelectedClientIdsChange(value: number[]): void {
    this.selectedClientIds = value
    this.selectedClientIdsChanged$.emit(value)
  }

  onDateRangeChange(value: Daterange): void {
    this.dateRange = value
    this.dateRangeChanged$.emit(value)
  }
}
